import {
  BlockedNetworkAttackTechnique,
  BlockedNetworkAttackTechniques,
  GetBlockedNetworkAttackTechniquesResponse,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  CommonThreatsNavigationHelper,
  NavigationItem,
  BlockedNetworkAttackTechniquesNavigationHelper,
} from '@nirvana/shared/navigation';
import { ThreatType } from '@nirvana/shared/dtos/types/shared/threats';
import { BaseThreatWidgetView } from '../../widget/BaseThreatWidgetView';

export class NetworkAttackView extends BaseThreatWidgetView {
  public latestData: BlockedNetworkAttackTechniques;
  public shadow: any;
  protected translations: any;

  getDomElements() {
    super.getDomElements();
    this.dom.$widgetTitle = this.shadow.querySelector(`.widget-title`);
    this.dom.$widgetTitleString = this.shadow.querySelector(`#blockedNetworkAttackTechniquesTitle`);
    this.dom.$networkAttackList = this.shadow.querySelector(`#networkAttackList`);
    this.dom.$initialAccessItem = this.shadow.querySelector(
      `[gz-network-attack-techniques="initialAccess"] [gz-list-element="name"]`
    );
    this.dom.$credentialAccessItem = this.shadow.querySelector(
      `[gz-network-attack-techniques="credentialAccess"] [gz-list-element="name"]`
    );
    this.dom.$discoveryItem = this.shadow.querySelector(
      `[gz-network-attack-techniques="discovery"] [gz-list-element="name"]`
    );
    this.dom.$lateralMovementItem = this.shadow.querySelector(
      `[gz-network-attack-techniques="lateralMovement"] [gz-list-element="name"]`
    );
    this.dom.$crimewareItem = this.shadow.querySelector(
      `[gz-network-attack-techniques="crimeware"] [gz-list-element="name"]`
    );
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getNetworkAttackWidgetStrings();
  }

  setTranslatedStrings() {
    const { title, categoriesMap } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
    this.dom.$initialAccessItem.innerHTML = categoriesMap.initialAccess;
    this.dom.$credentialAccessItem.innerHTML = categoriesMap.credentialAccess;
    this.dom.$discoveryItem.innerHTML = categoriesMap.discovery;
    this.dom.$lateralMovementItem.innerHTML = categoriesMap.lateralMovement;
    this.dom.$crimewareItem.innerHTML = categoriesMap.crimeware;
  }

  buildSuccessView(response: BackendResponseWithMeta<GetBlockedNetworkAttackTechniquesResponse>) {
    this.resetTemplateState(this.$wrapper);

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      this.latestData = result.data;
      for (const record in this.latestData) {
        if (this.latestData.hasOwnProperty(record)) {
          this.buildListElement(record);
        }
      }
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildListElement(record: string) {
    const technique: BlockedNetworkAttackTechnique = this.latestData[record];

    const $valueElement = this.shadow.querySelector(
      `[gz-network-attack-techniques="${record}"] [gz-list-element="value"]`
    );
    $valueElement.innerHTML = technique.values.currentValue;

    const $deltaElement = this.shadow.querySelector(
      `[gz-network-attack-techniques="${record}"] [gz-list-element="delta"]`
    );
    const deltaValue = technique.values.delta;

    if (!deltaValue) {
      $deltaElement.classList.add('hidden');
    }

    this.displayDeltaInElement($deltaElement, deltaValue, false);
  }

  private buildEmptyTemplate() {
    this.latestData = {
      credentialAccess: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1, // Irrelevant here, cannot drill down from empty state
      },
      crimeware: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1, // Irrelevant here, cannot drill down from empty state
      },
      discovery: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1, // Irrelevant here, cannot drill down from empty state
      },
      initialAccess: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1, // Irrelevant here, cannot drill down from empty state
      },
      lateralMovement: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1, // Irrelevant here, cannot drill down from empty state
      },
    };

    for (const record in this.latestData) {
      if (this.latestData.hasOwnProperty(record)) {
        this.buildListElement(record);
      }
    }
  }

  protected getNavigationItemForTitle(targets: Array<Target>, timeRange: number): NavigationItem {
    const threatTypes = this.getIntThreatTypesForTitle();
    const isPartner = Application.Main.company.isPartner();
    const filters = BlockedNetworkAttackTechniquesNavigationHelper.getFiltersForNavigation({
      timeRange: timeRange,
      threatTypes: threatTypes,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = BlockedNetworkAttackTechniquesNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([this.dom.$widgetTitle], filters, displayedColumns);
  }

  protected getNavigationItemsForWidgetBody(targets: Array<Target>, timeRange: number): Array<NavigationItem> {
    return Object.values(this.latestData).map((technique, listElementIndex: number) =>
      this.buildNavigationItemForListElement(technique, targets, timeRange, listElementIndex)
    );
  }

  private getIntThreatTypesForTitle(): Array<ThreatType> {
    return Object.values(this.latestData).map(technique => technique.threatType);
  }

  private buildNavigationItemForListElement(
    technique: BlockedNetworkAttackTechnique,
    targets: Array<Target>,
    timeRange: number,
    elementIndex: number
  ): NavigationItem {
    const $listElem = this.dom.$networkAttackList.children[elementIndex].querySelector('.item-details');
    const threatTypes = [technique.threatType];
    const isPartner = Application.Main.company.isPartner();
    const filters = BlockedNetworkAttackTechniquesNavigationHelper.getFiltersForNavigation({
      timeRange: timeRange,
      threatTypes: threatTypes,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = BlockedNetworkAttackTechniquesNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([$listElem], filters, displayedColumns);
  }
}
